/**
 * Copyright (c) 2026 Four Elements Corp. All rights reserved.
 * Licensed under the MIT License. See LICENSE in the project root for license information.
 * Author: Le Viet Quang <quangle@fourelementscorp.com>
 *
 * Translation Memory Bilingual Concordance Search DTOs & Validation Schemas (Task 8.2.3).
 */

import { z } from '@hono/zod-openapi';

export const ConcordanceSearchInEnum = z.enum(['source', 'target', 'both']);
export type ConcordanceSearchInType = z.infer<typeof ConcordanceSearchInEnum>;

export const ConcordanceItemSchema = z
  .object({
    id: z.string().openapi({ example: 'tm_01jh83n...' }),
    sourceText: z.string().openapi({ example: 'Cloudflare D1 is a serverless SQL database.' }),
    targetText: z.string().openapi({ example: 'Cloudflare D1 là cơ sở dữ liệu SQL không máy chủ.' }),
    highlightedSource: z.string().openapi({
      description: 'Source text with matched keywords wrapped in <mark> tags',
      example: '<mark>Cloudflare D1</mark> is a serverless SQL database.',
    }),
    highlightedTarget: z.string().openapi({
      description: 'Target text with matched keywords wrapped in <mark> tags (if matched)',
      example: '<mark>Cloudflare D1</mark> là cơ sở dữ liệu SQL không máy chủ.',
    }),
    sourceLang: z.string().openapi({ example: 'en' }),
    targetLang: z.string().openapi({ example: 'vi' }),
    hitCount: z.number().int().nonnegative().openapi({ example: 5 }),
  })
  .openapi('ConcordanceItem');

export type ConcordanceItemDto = z.infer<typeof ConcordanceItemSchema>;

export const ConcordanceSearchRequestSchema = z
  .object({
    query: z.string().trim().min(1).max(500).openapi({
      description: 'Search keyword or phrase (2 - 5 words) to find across TM history',
      example: 'serverless SQL',
    }),
    searchIn: ConcordanceSearchInEnum.default('both').optional().openapi({
      description: 'Target column to search in: source, target, or both',
      example: 'both',
    }),
    sourceLang: z.string().min(2).max(10).optional().openapi({
      description: 'Filter by source language (e.g. en)',
      example: 'en',
    }),
    targetLang: z.string().min(2).max(10).optional().openapi({
      description: 'Filter by target language (e.g. vi)',
      example: 'vi',
    }),
    limit: z.coerce.number().int().min(1).max(50).default(20).optional().openapi({
      description: 'Maximum number of concordance examples to return (default: 20)',
      example: 20,
    }),
  })
  .openapi('ConcordanceSearchRequest');

export type ConcordanceSearchRequestDto = z.infer<typeof ConcordanceSearchRequestSchema>;

export const ConcordanceSearchResponseSchema = z
  .object({
    query: z.string(),
    searchIn: ConcordanceSearchInEnum,
    total: z.number().int().nonnegative(),
    items: z.array(ConcordanceItemSchema),
  })
  .openapi('ConcordanceSearchResponse');

export type ConcordanceSearchResponseDto = z.infer<typeof ConcordanceSearchResponseSchema>;
